<template>
    <div data-component="TodayViewSession"
         :class="{
             'today-view-session--inactive': isInactive,
             'today-view-session--past': isPast
         }"
         class="today-view-session">
        <TodayViewSessionImage :event="event" />

        <SessionInfo :session="session"
                     :event="event"
                     :availability="availability"
                     :show-date="false" />

        <div class="today-view-session__btns desktop-only">
            <a class="btn btn-lg btn-white"
               :data-analytics-id="`Today / List / Session View Orders ${session.ID}`"
               :data-testid="`today-list-session-view-orders-${session.ID}`"
               @click.prevent="viewOrdersForSession">
                {{ l('TODAY_VIEW.VIEW_ORDERS') }}
            </a>

            <!-- The button must be only visually disabled not actually disabled to make the tooltip still work. -->
            <SquareBtn color="primary"
                       size="lg"
                       :data-analytics-id="`Today / List / Session Book Tickets ${session.ID}`"
                       :data-testid="`today-list-session-book-tickets-${session.ID}`"
                       :title-key="sellButtonDisabledReasonLangKey"
                       :visually-disabled="!sellButtonEnabled"
                       @click="sellTicketsForSession">
                <i class="fa-solid fa-shopping-cart"></i>
            </SquareBtn>
        </div>

        <div class="today-view-session__btns mobile-only">
            <a class="btn btn-white"
               @click="showMobileActionSheet">
                <i class="fa-solid fa-ellipsis-h"></i>
            </a>
        </div>
    </div>
</template>

<script>
import AppPath from '@/lib/Navigation/AppPath';
import SessionInfo from '@/epos/vue/modals/SessionPicker/SessionInfo';
import SessionWithEvent from '@/lib/Sessions/SessionWithEvent';
import SquareBtn from '@/vue/components/SquareBtn';
import TodayViewSessionImage from '@/epos/vue/pages/today/components/TodayViewSessionImage';
import { container } from '@/container';
import { createSessionSalesCutoffFilterForEvent } from '@/lib/Sessions/sessionSalesCutoffFilter';
import { MODAL_NAMES } from '@/vue/modals/MODAL_NAMES';

export default {
    name: 'TodayViewSession',

    components: {
        SquareBtn,
        TodayViewSessionImage,
        SessionInfo,
    },

    props: {
        session: {
            type: Object,
            required: true,
        },
        event: {
            type: Object,
            default: null,
        },
        availability: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            /**
             * Is this session still bookable according to session sales cutoffs?
             */
            isStillBookable: false,
        };
    },

    computed: {
        isPast() {
            return this.session.startTime < new Date();
        },

        isInactive() {
            return this.session.status === 'Inactive' || this.event.status === 'Inactive';
        },

        ticketsForEvent() {
            if (!this.$store.state.today.ticketsByEvent) {
                return [];
            }

            return this.$store.state.today.ticketsByEvent[this.event.ID] || [];
        },

        /**
         * The sell button is enabled if:
         * - The session is still bookable according to sales cutoff times
         * - There is at least one ticket that exists for this session
         * - There are spaces available
         *     - The spaces available check is skipped if offline and the button will always be enabled
         *       if there are tickets.
         *
         * @return {string|null}
         */
        sellButtonDisabledReasonLangKey() {
            if (!this.isStillBookable) {
                return 'TODAY_VIEW.SESSION_UNSELLABLE__SALES_CUTOFF';
            }

            if (this.ticketsForEvent.length < 1) {
                return 'TODAY_VIEW.SESSION_UNSELLABLE__NO_TICKETS';
            }

            if (this.$store.state.isOnline === true && this.availability.getSpacesBookable() < 1) {
                return 'TODAY_VIEW.SESSION_UNSELLABLE__NO_SPACES';
            }

            return null;
        },

        /**
         * @return {boolean}
         */
        sellButtonEnabled() {
            return this.sellButtonDisabledReasonLangKey === null;
        },
    },

    created() {
        const sessionSalesCutoffFilter = createSessionSalesCutoffFilterForEvent(this.event);
        this.isStillBookable = sessionSalesCutoffFilter(this.session);
    },

    methods: {
        async viewOrdersForSession() {
            const sessionWithEvent = new SessionWithEvent(this.session, this.event);

            // Tell the order search store to set up a fresh search for this session.
            await this.$store.dispatch(
                'orderSearch/setupSearchForSession',
                sessionWithEvent
            );

            // It would be nice to put the sessionID into the URL so that if you refresh the
            // order search page it will search for the same criteria again. But we need to
            // make the session and the event data available to the form so it can display
            // the session name and time to the user.
            container.navigationService.viewRedeem();
        },

        async sellTicketsForSession() {
            if (!this.sellButtonEnabled) {
                return;
            }

            // CartService's `setNewCart` method has been adapted to prompt when you call it when there are already
            // items in the cart on the sell screen.
            // We want to prompt the user here in case there are already items in the cart.
            // To make this work we'll just create an empty cart here and call setNewCart with that to trigger
            // the prompt if necessary. If the user chooses to "keep", the existing cart contents gets merged into
            // the empty cart which is fine. If they choose to "clear", the existing cart is replaced with an empty
            // cart, which is also fine.
            const newCart = container.cartService.createEmptyCart();
            const cartSet = await container.cartService.setNewCart(newCart, true);
            if (cartSet) {
                await container.cartService.modifyCurrentCart((currentCart) => {
                    // Set up the sell state to auto show the ticket picker for this session,
                    // and to return to the today view after the ticket picker is closed.
                    currentCart.sellState.autoAddSessionID = this.session.ID;
                    currentCart.sellState.cancelTicketPickerReturnPath = new AppPath('/today');

                    // Set where to redirect the user to after they have added an item to the cart.
                    // If the device has manage group bookings enabled we will return them to the today screen,
                    // after adding to the cart and show a toast. Otherwise, they'll stay on the sell screen.
                    if (container.currentDevice.device.manageGroupBookings) {
                        currentCart.sellState.addItemReturnPath = new AppPath('/today');
                        // Set to display a success message when the user adds an item to the cart.
                        currentCart.sellState.showItemAddedToast = true;
                    }
                });

                container.navigationService.viewSell();
            }
        },

        showMobileActionSheet() {
            const self = this;

            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES.ACTION_SHEET,
                {
                    buttons: [
                        {
                            label: 'TODAY_VIEW.BOOK_TICKETS',
                            color: 'primary',
                            visuallyDisabled: !this.sellButtonEnabled,
                            titleKey: this.sellButtonDisabledReasonLangKey,
                            click() {
                                self.sellTicketsForSession();
                            },
                        },
                        {
                            label: 'TODAY_VIEW.VIEW_ORDERS',
                            color: 'white',
                            click() {
                                self.viewOrdersForSession();
                            },
                        },
                    ],
                }
            );
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

.today-view-session {
    display: flex;
    align-items: center;
    padding: @gutter-width-mobile (@gutter-width-mobile - 5px);
    border-bottom: 1px @dt-light-grey solid;
    border-left: 5px transparent solid; // This border changes to yellow for inactive sessions.
    border-right: 5px transparent solid; // To make it even.

    &--inactive {
        border-left-color: @brand-warning;
    }

    &--past {
        opacity: .6;
        background-color: @mid-grey;
    }

    &__image {
        margin-right: 10px;
    }

    .session-info {
        flex-grow: 1;
        flex-shrink: 1;
    }

    &__btns {
        margin-left: @gutter-width;

        .btn {
            &:not(:first-child) {
                margin-left: @half-gutter-width;
            }
        }
    }
}

@media (min-width: @non-mobile-min) {
    .today-view-session {
        height: 81px;
        padding: @gutter-width (@gutter-width - 5px);

        &--past {
            opacity: .8;
        }

        &__image {
            margin-right: @gutter-width;
        }
    }
}
</style>
